import axios from 'axios'
import { useAuthStore } from '../store/authStore'

/**
 * 세션(토큰) 갱신 — 두 경로가 이 함수 하나로 모인다.
 *  1) client.ts 인터셉터: API 가 401 을 줬을 때
 *  2) startSessionKeepalive: 액세스 토큰 만료 전에 선제적으로
 * 리프레시 토큰은 rotation(1회용)이라 동시에 두 번 보내면 뒤의 것이 실패해 로그아웃되므로,
 * 진행 중인 갱신이 있으면 그 Promise 를 같이 기다린다.
 */

/** 만료 이 시간 전에 미리 갱신 */
const REFRESH_LEAD_MS = 60_000
/** 갱신 실패(네트워크 등) 시 재시도 간격 */
const RETRY_MS = 30_000

let inflight: Promise<string | null> | null = null

export function refreshSession(): Promise<string | null> {
  if (inflight) return inflight
  inflight = doRefresh().finally(() => { inflight = null })
  return inflight
}

async function doRefresh(): Promise<string | null> {
  const { refreshToken, setTokens } = useAuthStore.getState()
  if (!refreshToken) return null
  try {
    const { data } = await axios.post('/api/auth/refresh', { refreshToken })
    setTokens(data.accessToken, data.refreshToken)
    return data.accessToken as string
  } catch (err) {
    // 서버가 거부(4xx: 만료·폐기·강제 만료)했으면 세션 종료. 네트워크/5xx 는 일시 장애로 보고 세션을 유지한다 —
    // 다음 401 또는 keepalive 재시도에서 다시 시도한다.
    if (axios.isAxiosError(err) && err.response && err.response.status < 500) {
      endSession()
    }
    return null
  }
}

export function endSession() {
  useAuthStore.getState().logout()
  if (!window.location.pathname.startsWith('/login')) window.location.href = '/login'
}

const tokenExpiryMs = (token: string): number | null => {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    const exp = JSON.parse(atob(payload))?.exp
    return typeof exp === 'number' ? exp * 1000 : null
  } catch { return null }
}

/**
 * 액세스 토큰 만료 1분 전에 선제 갱신하는 타이머. 401 을 기다리지 않으므로 SignalR 재접속이나
 * 저장 요청이 만료 직후에 실패하는 일이 없고, API 호출 없이 화면만 보고 있어도 세션이 이어진다.
 * 탭이 잠들었다 깨어나면(visibilitychange) 즉시 점검한다. 앱 루트에서 한 번만 호출.
 */
export function startSessionKeepalive(): () => void {
  let timer: ReturnType<typeof setTimeout> | undefined

  const schedule = () => {
    clearTimeout(timer)
    const { accessToken, refreshToken } = useAuthStore.getState()
    if (!accessToken || !refreshToken) return
    const exp = tokenExpiryMs(accessToken)
    if (exp == null) return
    const delay = Math.max(exp - Date.now() - REFRESH_LEAD_MS, 0)
    timer = setTimeout(async () => {
      const ok = await refreshSession()
      // 성공하면 store 구독이 새 토큰 기준으로 다시 예약한다. 실패(일시 장애)면 잠시 후 재시도.
      if (!ok && useAuthStore.getState().accessToken) {
        timer = setTimeout(schedule, RETRY_MS)
      }
    }, delay)
  }

  const onVisible = () => { if (document.visibilityState === 'visible') schedule() }

  const unsubscribe = useAuthStore.subscribe((s, prev) => {
    if (s.accessToken !== prev.accessToken) schedule()
  })
  document.addEventListener('visibilitychange', onVisible)
  schedule()

  return () => {
    clearTimeout(timer)
    unsubscribe()
    document.removeEventListener('visibilitychange', onVisible)
  }
}
